import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { useI18n } from '@/i18n/I18nProvider';
import { cn } from '@/shared/lib/cn';
import { formatNumber, formatSignedPercent } from '@/shared/lib/format';
import { hexToRgba } from '@/shared/lib/nav-accent';
import { Sparkline } from '../Sparkline';
import type { HeroStatData, HeroTone } from './types';
import { useCountUp } from './useCountUp';

const TONE_TEXT: Record<HeroTone, string> = {
  default: 'text-white',
  success: 'text-emerald-300',
  warning: 'text-amber-300',
  danger: 'text-rose-300',
};

export function HeroStatSkeleton() {
  return (
    <div className="rounded-xl bg-white/[0.07] p-3.5 ring-1 ring-white/10">
      <div className="h-3 w-20 animate-pulse rounded bg-white/15" />
      <div className="mt-3 h-7 w-28 animate-pulse rounded bg-white/20" />
      <div className="mt-2.5 h-2.5 w-16 animate-pulse rounded bg-white/10" />
    </div>
  );
}

function Delta({ delta }: { delta: NonNullable<HeroStatData['delta']> }) {
  const { locale } = useI18n();
  const { value, inverse, label } = delta;
  const flat = Math.abs(value) < 0.05;
  const good = flat ? null : inverse ? value < 0 : value > 0;
  const Icon = flat ? Minus : value > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-bold tabular-nums',
        good === null && 'bg-white/10 text-white/70',
        good === true && 'bg-emerald-400/15 text-emerald-300',
        good === false && 'bg-rose-400/15 text-rose-300',
      )}
    >
      <Icon className="size-3" aria-hidden />
      {formatSignedPercent(value, locale, 1)}
      {label && <span className="ml-1 hidden font-medium text-white/55 sm:inline">{label}</span>}
    </span>
  );
}

/** Pojedynczy kafelek wskaźnika w nagłówku: wartość (count-up), zmiana, trend i pasek realizacji celu. */
export function HeroStat({ stat, accent, index = 0 }: { stat: HeroStatData; accent: string; index?: number }) {
  const { locale } = useI18n();
  const numeric = typeof stat.value === 'number' ? stat.value : null;
  const animated = useCountUp(numeric, stat.countUpMs);
  const fmt = stat.format ?? ((n: number) => formatNumber(Math.round(n), locale));
  const text = numeric !== null ? (animated === null ? '—' : fmt(animated)) : stat.value === null ? '—' : stat.value;
  const progress = stat.progress == null ? null : Math.max(0, Math.min(1, stat.progress));

  const body = (
    <>
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <span className="flex min-w-0 items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-white/60">
          {stat.icon && (
            <span className="shrink-0 [&_svg]:size-3.5" style={{ color: accent }}>
              {stat.icon}
            </span>
          )}
          <span className="leading-tight sm:truncate">{stat.label}</span>
        </span>
        {stat.delta && <Delta delta={stat.delta} />}
      </div>

      <div className="relative mt-1">
        {stat.spark && stat.spark.length > 1 && (
          <span aria-hidden className="pointer-events-none absolute -bottom-1 right-0 hidden h-7 w-20 opacity-60 sm:block" style={{ color: accent }}>
            <Sparkline data={stat.spark} className="text-inherit" />
          </span>
        )}
        {/* Bez ucinania „…”: rozmiar dopasowuje się do szerokości kafelka (cqi), a w ostateczności tekst się zawija. */}
        <span
          className={cn(
            'relative block break-words text-[length:clamp(1.0625rem,0.55rem+7cqi,1.4375rem)] font-extrabold leading-[1.05] tracking-tight tabular-nums',
            TONE_TEXT[stat.tone ?? 'default'],
          )}
        >
          {text}
        </span>
      </div>

      {progress !== null && (
        <div
          className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/10"
          role="progressbar"
          aria-valuenow={Math.round(progress * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full transition-[width] duration-700 ease-out"
            style={{ width: `${progress * 100}%`, background: `linear-gradient(90deg, ${hexToRgba(accent, 0.7)}, ${accent})` }}
          />
        </div>
      )}
      {stat.hint && <p className="mt-1 text-[10.5px] leading-snug text-white/50">{stat.hint}</p>}
    </>
  );

  const cls =
    'group block animate-fade-in rounded-xl [container-type:inline-size] bg-white/[0.07] px-3 py-2.5 ring-1 ring-white/10 backdrop-blur-sm transition-all duration-200 [animation-fill-mode:backwards]';
  const style = { animationDelay: `${index * 40}ms` };

  return stat.to ? (
    <Link
      to={stat.to}
      className={cn(cls, 'hover:-translate-y-0.5 hover:bg-white/[0.12] hover:ring-white/25 focus-visible:ring-2 focus-visible:ring-white/50')}
      style={style}
    >
      {body}
    </Link>
  ) : (
    <div className={cls} style={style}>
      {body}
    </div>
  );
}
